import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { DirectoryEntry, UserGroupMemberEntry, UserGroupSummary } from '@system-atlas/shared';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { Alert, Badge, Button, Card, ConfirmDialog, Label, Modal, Spinner, TextInput } from './ui';
import { useToast } from './toast';

/**
 * Verwaltung der Benutzergruppen. Admins koennen Gruppen anlegen, umbenennen,
 * loeschen und Mitglieder samt Manager-Rolle pflegen. Gruppen-Manager (Nicht-Admins)
 * sehen serverseitig nur ihre eigenen Gruppen und duerfen dort Mitglieder pflegen,
 * aber keine Manager-Rolle vergeben.
 */
export function UserGroupsAdmin() {
  const { t } = useTranslation();
  const { user: me } = useAuth();
  const toast = useToast();
  const isAdmin = me?.role === 'admin' || me?.role === 'superadmin';

  const [groups, setGroups] = useState<UserGroupSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<UserGroupSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserGroupSummary | null>(null);
  const [membersTarget, setMembersTarget] = useState<UserGroupSummary | null>(null);

  const load = async () => {
    try {
      const { groups: rows } = await api.listUserGroups();
      setGroups(rows);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <Card className="mt-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-md">
          <h2 className="text-lg font-semibold">{t('userGroups.title')}</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{t('userGroups.subtitle')}</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreateOpen(true)}>{t('userGroups.create')}</Button>
        )}
      </div>

      {error && (
        <div className="mt-4">
          <Alert kind="error">{error}</Alert>
        </div>
      )}

      <div className="mt-4">
        {!groups ? (
          <Spinner />
        ) : groups.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('userGroups.empty')}</p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-700">
            {groups.map((g) => (
              <li key={g.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-medium">
                    {g.name}
                    {g.isManager && <Badge tone="green">{t('userGroups.managerBadge')}</Badge>}
                  </p>
                  {g.description && (
                    <p className="truncate text-sm text-slate-500 dark:text-slate-400">{g.description}</p>
                  )}
                  <p className="text-xs text-slate-400">{t('userGroups.memberCount', { count: g.memberCount })}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="secondary" onClick={() => setMembersTarget(g)}>
                    {t('userGroups.members')}
                  </Button>
                  {isAdmin && (
                    <>
                      <Button variant="ghost" onClick={() => setRenameTarget(g)}>
                        {t('userGroups.rename')}
                      </Button>
                      <Button variant="ghost" onClick={() => setDeleteTarget(g)}>
                        {t('userGroups.delete')}
                      </Button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {isAdmin && (
        <UserGroupFormModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSaved={async () => {
            setCreateOpen(false);
            await load();
            toast.success(t('userGroups.createdToast'));
          }}
        />
      )}

      {isAdmin && (
        <UserGroupFormModal
          open={!!renameTarget}
          group={renameTarget}
          onClose={() => setRenameTarget(null)}
          onSaved={async () => {
            setRenameTarget(null);
            await load();
            toast.success(t('userGroups.updatedToast'));
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('userGroups.delete')}
        message={t('userGroups.confirmDelete', { name: deleteTarget?.name ?? '' })}
        confirmLabel={t('common.delete')}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          const group = deleteTarget;
          setDeleteTarget(null);
          if (!group) return;
          try {
            await api.deleteUserGroup(group.id);
            await load();
            toast.success(t('userGroups.deletedToast'));
          } catch (err) {
            toast.error(err instanceof ApiError ? err.message : t('errors.generic'));
          }
        }}
      />

      <MembersModal
        group={membersTarget}
        isAdmin={isAdmin}
        onClose={() => setMembersTarget(null)}
        onChanged={load}
      />
    </Card>
  );
}

/** Anlegen/Umbenennen einer Benutzergruppe (Name + optionale Beschreibung). */
function UserGroupFormModal({
  open,
  group,
  onClose,
  onSaved,
}: {
  open: boolean;
  group?: UserGroupSummary | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(group?.name ?? '');
      setDescription(group?.description ?? '');
    }
  }, [open, group?.id]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { name: name.trim(), description: description.trim() || null };
      if (group) await api.updateUserGroup(group.id, payload);
      else await api.createUserGroup(payload);
      await onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} title={group ? t('userGroups.renameTitle') : t('userGroups.createTitle')} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div>
          <Label htmlFor="ug-name">{t('userGroups.nameLabel')}</Label>
          <TextInput id="ug-name" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="ug-desc">{t('userGroups.descriptionLabel')}</Label>
          <TextInput id="ug-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? t('common.pleaseWait') : t('common.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Mitglieder einer Benutzergruppe verwalten (hinzufuegen, entfernen, Manager-Rolle). */
function MembersModal({
  group,
  isAdmin,
  onClose,
  onChanged,
}: {
  group: UserGroupSummary | null;
  isAdmin: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [members, setMembers] = useState<UserGroupMemberEntry[]>([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DirectoryEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (id: string) => {
    try {
      const { members: rows } = await api.listUserGroupMembers(id);
      setMembers(rows);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  };

  useEffect(() => {
    if (group) {
      setError(null);
      setQuery('');
      setResults([]);
      void load(group.id);
    }
  }, [group?.id]);

  // Typeahead nur ueber Benutzer (Mitglieder sind immer Personen).
  useEffect(() => {
    if (!group) return;
    const term = query.trim();
    if (!term) {
      setResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const { entries } = await api.searchDirectory(term, 'user');
        const memberIds = new Set(members.map((m) => m.userId));
        setResults(entries.filter((e) => !memberIds.has(e.id)));
      } catch {
        setResults([]);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query, group, members]);

  const act = async (fn: () => Promise<unknown>) => {
    if (!group) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load(group.id);
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const addMember = (entry: DirectoryEntry) =>
    act(async () => {
      await api.addUserGroupMember(group!.id, { userId: entry.id });
      setQuery('');
      setResults([]);
      toast.success(t('userGroups.memberAddedToast'));
    });

  return (
    <Modal open={!!group} title={t('userGroups.membersTitle', { name: group?.name ?? '' })} onClose={onClose}>
      <div className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}

        <div>
          <Label htmlFor="ug-member-search">{t('userGroups.addMember')}</Label>
          <TextInput
            id="ug-member-search"
            value={query}
            placeholder={t('userGroups.searchPlaceholder')}
            onChange={(e) => setQuery(e.target.value)}
          />
          {results.length > 0 && (
            <ul className="mt-1 max-h-44 overflow-auto rounded border border-slate-200 text-sm dark:border-slate-700">
              {results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    disabled={busy}
                    className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-slate-50 disabled:opacity-50 dark:hover:bg-slate-800"
                    onClick={() => void addMember(r)}
                  >
                    <span>
                      {r.label} <span className="text-slate-400">({r.sublabel})</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="divide-y divide-slate-100 dark:divide-slate-700">
          {members.length === 0 ? (
            <p className="py-3 text-sm text-slate-500 dark:text-slate-400">{t('userGroups.noMembers')}</p>
          ) : (
            members.map((m) => (
              <div key={m.userId} className="flex items-center justify-between gap-2 py-2 text-sm">
                <span className="min-w-0">
                  <span className="font-medium">{m.displayName}</span>{' '}
                  <span className="text-slate-400">({m.email})</span>
                </span>
                <span className="flex items-center gap-2">
                  <Badge tone={m.role === 'manager' ? 'green' : 'neutral'}>
                    {t(`userGroups.${m.role}`)}
                  </Badge>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      className="px-2 py-0.5 text-xs"
                      disabled={busy}
                      onClick={() =>
                        void act(async () => {
                          await api.setUserGroupMemberRole(
                            group!.id,
                            m.userId,
                            m.role === 'manager' ? 'member' : 'manager',
                          );
                        })
                      }
                    >
                      {m.role === 'manager' ? t('userGroups.removeManager') : t('userGroups.makeManager')}
                    </Button>
                  )}
                  <button
                    type="button"
                    aria-label={t('userGroups.remove')}
                    title={t('userGroups.remove')}
                    disabled={busy}
                    className="rounded px-1 text-slate-400 transition-colors hover:text-red-600 disabled:opacity-50 dark:hover:text-red-400"
                    onClick={() => void act(async () => { await api.removeUserGroupMember(group!.id, m.userId); })}
                  >
                    ×
                  </button>
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
